/**
 * ReadingGuideFeature - Visual reading guide with 3 types
 * Types: horizontal, ruler, spotlight
 */
import BaseFeature from './BaseFeature.js';

export default class ReadingGuideFeature extends BaseFeature {
    constructor(widget, options = {}) {
        super(widget, options);
        this.guideType = options.defaultType || 'horizontal';
        this.color = options.color || '#FF0000';
        this.thickness = options.thickness || 2;
        this.opacity = options.opacity || 0.7;
        this.guideElements = [];
        this._mouseMoveHandler = null;
    }

    enable() {
        super.enable();
        // Restore saved settings
        const saved = this.widget.storage.get('reading_guide_settings');
        if (saved) {
            this.guideType = saved.type || this.guideType;
            this.color = saved.color || this.color;
            this.thickness = saved.thickness || this.thickness;
            this.opacity = saved.opacity || this.opacity;
        }
        this._createGuide();
        this._bindMouse();
    }

    disable() {
        super.disable();
        this._removeGuide();
        this._unbindMouse();
    }

    /**
     * Set guide type
     */
    setGuideType(type) {
        if (['horizontal', 'ruler', 'spotlight'].includes(type)) {
            this.guideType = type;
            if (this.isActive) {
                this._removeGuide();
                this._createGuide();
            }
            this._saveSettings();
        }
    }

    /**
     * Set guide color
     */
    setColor(color) {
        this.color = color;
        this._updateStyles();
        this._saveSettings();
    }

    /**
     * Set guide thickness
     */
    setThickness(thickness) {
        this.thickness = thickness;
        this._updateStyles();
        this._saveSettings();
    }

    /**
     * Set guide opacity
     */
    setOpacity(opacity) {
        this.opacity = opacity;
        this._updateStyles();
        this._saveSettings();
    }

    /**
     * Create guide DOM elements based on type
     */
    _createGuide() {
        this._removeGuide();

        switch (this.guideType) {
            case 'horizontal':
                this._createHorizontalGuide();
                break;
            case 'ruler':
                this._createRulerGuide();
                break;
            case 'spotlight':
                this._createSpotlightGuide();
                break;
        }
    }

    /**
     * Create horizontal line guide
     */
    _createHorizontalGuide() {
        const line = document.createElement('div');
        line.className = 'dhA11y-reading-guide dhA11y-reading-guide-horizontal';
        line.style.cssText = `
            position: fixed;
            left: 0;
            right: 0;
            height: ${this.thickness}px;
            background-color: ${this.color};
            opacity: ${this.opacity};
            pointer-events: none;
            z-index: 999994;
            transition: top 0.05s linear;
            display: none;
        `;
        document.body.appendChild(line);
        this.guideElements.push(line);
    }

    /**
     * Create ruler guide (top and bottom lines)
     */
    _createRulerGuide() {
        const gap = 30;
        ['top', 'bottom'].forEach(pos => {
            const line = document.createElement('div');
            line.className = `dhA11y-reading-guide dhA11y-reading-guide-ruler dhA11y-reading-guide-ruler-${pos}`;
            line.style.cssText = `
                position: fixed;
                left: 0;
                right: 0;
                height: ${this.thickness}px;
                background-color: ${this.color};
                opacity: ${this.opacity};
                pointer-events: none;
                z-index: 999994;
                transition: top 0.05s linear;
                display: none;
            `;
            line.dataset.rulerPos = pos;
            line.dataset.gap = gap;
            document.body.appendChild(line);
            this.guideElements.push(line);
        });
    }

    /**
     * Create spotlight guide (dims everything except focused area)
     */
    _createSpotlightGuide() {
        const spotlightHeight = 60;
        const overlay = document.createElement('div');
        overlay.className = 'dhA11y-reading-guide dhA11y-reading-guide-spotlight';
        overlay.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            pointer-events: none;
            z-index: 999994;
            display: none;
        `;
        overlay.dataset.spotlightHeight = spotlightHeight;
        document.body.appendChild(overlay);
        this.guideElements.push(overlay);
    }

    /**
     * Bind mouse move handler
     */
    _bindMouse() {
        this._mouseMoveHandler = this._throttle((e) => {
            this._updatePosition(e.clientY);
        }, 16);

        document.addEventListener('mousemove', this._mouseMoveHandler);

        // Show guides on first move
        this._firstMoveHandler = () => {
            this.guideElements.forEach(el => { el.style.display = ''; });
            document.removeEventListener('mousemove', this._firstMoveHandler);
        };
        document.addEventListener('mousemove', this._firstMoveHandler);
    }

    /**
     * Unbind mouse move handler
     */
    _unbindMouse() {
        if (this._mouseMoveHandler) {
            document.removeEventListener('mousemove', this._mouseMoveHandler);
            this._mouseMoveHandler = null;
        }
        if (this._firstMoveHandler) {
            document.removeEventListener('mousemove', this._firstMoveHandler);
            this._firstMoveHandler = null;
        }
    }

    /**
     * Update guide position based on cursor Y
     */
    _updatePosition(y) {
        switch (this.guideType) {
            case 'horizontal':
                if (this.guideElements[0]) {
                    this.guideElements[0].style.top = y + 'px';
                }
                break;
            case 'ruler':
                this.guideElements.forEach(el => {
                    const gap = parseInt(el.dataset.gap) || 30;
                    if (el.dataset.rulerPos === 'top') {
                        el.style.top = (y - gap) + 'px';
                    } else {
                        el.style.top = (y + gap) + 'px';
                    }
                });
                break;
            case 'spotlight':
                if (this.guideElements[0]) {
                    const h = parseInt(this.guideElements[0].dataset.spotlightHeight) || 60;
                    const halfH = h / 2;
                    this.guideElements[0].style.background = `
                        linear-gradient(
                            to bottom,
                            rgba(0,0,0,0.7) 0%,
                            rgba(0,0,0,0.7) ${y - halfH}px,
                            transparent ${y - halfH}px,
                            transparent ${y + halfH}px,
                            rgba(0,0,0,0.7) ${y + halfH}px,
                            rgba(0,0,0,0.7) 100%
                        )
                    `;
                }
                break;
        }
    }

    /**
     * Update styles of existing guide elements
     */
    _updateStyles() {
        this.guideElements.forEach(el => {
            if (!el.classList.contains('dhA11y-reading-guide-spotlight')) {
                el.style.height = this.thickness + 'px';
                el.style.backgroundColor = this.color;
            }
            el.style.opacity = this.opacity;
        });
    }

    /**
     * Remove guide elements
     */
    _removeGuide() {
        this.guideElements.forEach(el => el.remove());
        this.guideElements = [];
    }

    /**
     * Save settings to storage
     */
    _saveSettings() {
        this.widget.storage.set('reading_guide_settings', {
            type: this.guideType,
            color: this.color,
            thickness: this.thickness,
            opacity: this.opacity
        });
    }

    /**
     * Simple throttle utility
     */
    _throttle(fn, delay) {
        let last = 0;
        return function (...args) {
            const now = Date.now();
            if (now - last >= delay) {
                last = now;
                fn.apply(this, args);
            }
        };
    }
}
